import styled from 'styled-components';
import React, { Component } from 'react';
import { Info } from '@material-ui/icons';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { replace } from 'redux-first-history';
import { compose } from 'react-recompose';
import { isEmpty, map } from 'lodash-es';
import { parse } from 'query-string';
import {
    Alert,
    ContentLoader,
    CustomTable,
    CustomTableRowsProps,
    EmptyState,
    formatNumber,
    MobileCardRowsProps,
    MobileTable,
    Pagination,
    palette,
    Paper,
    ThrottleDot,
    Tooltip,
} from '@dev/ee-components';

import InProgressInterface from '../InProgressInterface';
import { ApplicationState, BrowserInterface } from '../../../../../store';
import { DEFAULT_PER_PAGE_COUNT } from '../../../../../services/constants';
import { addParamToUrl } from '../../../../../services/tools/handleUrlParams';
import { CampaignsInterface } from '../../../../../store/campaigns/types';
import { TableHeaderProps } from '../../../CampaignsList/CampaignsListInterface';

type StyledProps = {
    isMobile?: boolean;
};

const AlertWrapper = styled.div<StyledProps>`
    padding: ${({ isMobile }) => (isMobile ? '0 20px 12px' : '0 32px 12px')};
`;

const TabWrapper = styled.div`
    padding-top: 32px;
`;

const ThrottleDotWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const TooltipWrapper = styled.div`
    padding-right: 4px;
`;

const MobileTableContainer = styled.div`
    margin: 24px 20px 0;

    & > div {
        padding-bottom: 2px;
        background-color: #fff;
    }
`;
interface StateToProps {
    browser?: BrowserInterface;
    campaigns?: CampaignsInterface;
}
type Props = StateToProps & DispatchProps & InProgressInterface;

const mapStateToProps = (state: ApplicationState, ownProps: Props): StateToProps => ({
    ...ownProps,
    browser: state.browser,
    campaigns: state.campaigns,
});

const dispatchProps = {
    Replace: replace,
};

type DispatchProps = {
    Replace(param: Record<string, string> | string): void;
};

const getColumnsWidth = (): Record<string, string> => ({
    domain: '25%',
    inprogress: '12%',
    waiting: '12%',
    readytosend: '12%',
    attemptrate: '12%',
    successrate: '15%',
    throttling: '10%',
});

class CampaignReportsInProgressOverview extends Component<Props> {
    public state = {
        perPage: DEFAULT_PER_PAGE_COUNT,
        activePage: 1,
        paginationLimitFrom: 0,
        paginationLimitTo: 25,
    };

    public componentDidMount(): void {
        const query = parse(location.search);

        const paginationState = {
            ...(!isEmpty(query.perPage) ? { perPage: Number(query.perPage) } : {}),
            ...(!isEmpty(query.activePage) ? { activePage: Number(query.activePage) } : {}),
            ...(!isEmpty(query.paginationLimitFrom) ? { paginationLimitFrom: Number(query.paginationLimitFrom) } : {}),
            ...(!isEmpty(query.paginationLimitTo) ? { paginationLimitTo: Number(query.paginationLimitTo) } : {}),
        };

        if (!isEmpty(paginationState)) {
            this.setState(paginationState);
        }
    }

    private getTableRows = (): CustomTableRowsProps[] => {
        const {
            campaigns: { inProgressCampaign },
        } = this.props;

        const { paginationLimitFrom, paginationLimitTo } = this.state;

        if (!inProgressCampaign?.Domains) {
            return [];
        }

        return map(inProgressCampaign?.Domains.slice(paginationLimitFrom, paginationLimitTo), (singleDomain) => {
            const getThrottlingStatus = (throttlingStatus) => {
                switch (throttlingStatus) {
                    case 'Red':
                        return palette.red[6];
                    case 'Yellow':
                        return palette.orange[5];
                    default:
                        return palette.teal[5];
                }
            };

            return {
                domain: singleDomain?.Domain,
                inprogress: <div style={{ textAlign: 'right' }}>{formatNumber(singleDomain?.Count)}</div>,
                waiting: (
                    <div style={{ textAlign: 'right' }}>
                        {formatNumber((singleDomain?.Count || 0) - (singleDomain?.Ready || 0))}
                    </div>
                ),
                readytosend: <div style={{ textAlign: 'right' }}>{formatNumber(singleDomain?.Ready)}</div>,
                attemptrate: <div style={{ textAlign: 'right' }}>{`${singleDomain?.Rate.toFixed(1)}/s`}</div>,
                successrate: <div style={{ textAlign: 'right' }}>{`${singleDomain?.SentRate.toFixed(1)}/s`}</div>,
                throttling: (
                    <ThrottleDotWrapper>
                        {<ThrottleDot color={getThrottlingStatus(singleDomain?.ThrottlingStatus)} />}
                    </ThrottleDotWrapper>
                ),
            };
        });
    };

    private getTableHeader = (): TableHeaderProps[] => {
        const { t } = this.props;

        const tooltip = (id: string, title: string): JSX.Element => (
            <TooltipWrapper>
                <Tooltip PopperProps={{ disablePortal: false }} id={id} title={title} color={palette.basic.paper}>
                    <Info fontSize="small" />
                </Tooltip>
            </TooltipWrapper>
        );

        return [
            {
                id: 'domain',
                label: t('domain'),
            },
            {
                id: 'inprogress',
                label: t('inprogress'),
                headerAlign: 'right',
            },
            {
                id: 'waiting',
                label: t('waiting'),
                headerAlign: 'right',
            },
            {
                id: 'readytosend',
                label: t('readytosend'),
                headerAlign: 'right',
            },
            {
                id: 'attemptrate',
                label: t('attemptrate'),
                headerAlign: 'right',
            },
            {
                id: 'successrate',
                label: t('successrate'),
                filterIcon: tooltip('id', t('thespeedatwhichtooltip')),
                headerAlign: 'right',
            },
            {
                id: 'throttling',
                label: t('throttling'),
                filterIcon: tooltip('id', t('statusofemaildeliveryratetooltip')),
                headerAlign: 'right',
            },
        ];
    };

    private mobileTableHeaders = () => {
        const { t } = this.props;

        return [
            t('domain'),
            t('inprogress'),
            t('waiting'),
            t('readytosend'),
            t('attemptrate'),
            t('successrate'),
            t('successrate'),
        ];
    };

    private mobileTableRows = (): CustomTableRowsProps[] => {
        const { campaigns } = this.props;
        const { perPage } = this.state;

        const SkeletonTemplate = {
            domain: <ContentLoader mode="row" />,
            inprogress: <ContentLoader mode="row" />,
            waiting: <ContentLoader mode="row" />,
            readytosend: <ContentLoader mode="row" />,
            attemptrate: <ContentLoader mode="row" />,
            successrate: <ContentLoader mode="row" />,
            throttling: <ContentLoader mode="row" />,
        };

        const SkeletonRows = map([...new Array(perPage)], (): MobileCardRowsProps => SkeletonTemplate);

        return campaigns.loading ? SkeletonRows : this.getTableRows();
    };

    private addPaginationQuery(toAdd: Record<string, any>): void {
        const { Replace } = this.props;
        const newPath = addParamToUrl(toAdd);
        Replace({ search: newPath });
    }

    private changePage = (activePage: number, perPage: number): void => {
        const calcPaginationLimitTo = perPage * activePage;

        this.setState({
            perPage,
            activePage,
            paginationLimitFrom: calcPaginationLimitTo - perPage,
            paginationLimitTo: calcPaginationLimitTo,
        });
        this.addPaginationQuery({
            activePage,
            perPage,
            paginationLimitFrom: calcPaginationLimitTo - perPage,
            paginationLimitTo: calcPaginationLimitTo,
        });
    };

    private renderMobileTable = (): JSX.Element => {
        const {
            t,
            campaigns,
            campaigns: { inProgressCampaign },
        } = this.props;
        const headers = this.mobileTableHeaders();
        const rows = this.mobileTableRows();

        if (!campaigns?.loading && isEmpty(inProgressCampaign?.Domains)) {
            return <EmptyState text={t('thereisnodatatodisplay')} />;
        }

        return (
            <MobileTableContainer>
                <MobileTable headers={headers} rows={rows} footer={null} />
            </MobileTableContainer>
        );
    };

    public render(): JSX.Element {
        const { t } = this.props;
        const { activePage, perPage } = this.state;
        const {
            campaigns,
            campaigns: { inProgressCampaign },
            browser,
        } = this.props;
        const isMobile = browser.lessThan.hd;

        return (
            <TabWrapper>
                <Paper padding={{ top: 24 }}>
                    <AlertWrapper isMobile={isMobile}>
                        <Alert severity="info" body={<div>{t('allinprogressestimates')}</div>} />
                    </AlertWrapper>

                    {isMobile ? (
                        this.renderMobileTable()
                    ) : (
                        <CustomTable
                            headers={this.getTableHeader()}
                            rows={this.getTableRows()}
                            isEmpty={inProgressCampaign?.Domains?.length === 0 && !campaigns.loading}
                            loading={campaigns.loading}
                            stickyHeader={true}
                            rowPadding={32}
                            rowHeight={60}
                            hideCheckboxes={true}
                            columnsWidth={getColumnsWidth()}
                            emptyStateProps={{ text: t('emptystate') }}
                            disableRowClick={true}
                            disableFirstRowBorderTop={true}
                        />
                    )}

                    <Pagination
                        perPage={perPage}
                        activePage={activePage}
                        dataLength={inProgressCampaign?.Domains?.length || 0}
                        handlePaginationChange={(activePage, perPage): void => this.changePage(activePage, perPage)}
                    />
                </Paper>
            </TabWrapper>
        );
    }
}

export default compose(
    withTranslation(),
    connect<StateToProps, DispatchProps, Props>(mapStateToProps, dispatchProps)
)(CampaignReportsInProgressOverview);
